import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Anchor, LogOut, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const LOGO = "https://customer-assets-0z36b82j.emergentagent.net/job_3746c8ad-73a3-4cbb-87f3-7b3af248f392/artifacts/kwgzn05w_image.png";
const LOGO2 = "https://customer-assets-39nsmqrw.emergentagent.net/job_vessel-tracker-demo-1/artifacts/fa2sce7j_image.png";

const SUPPLY_LINKS = [
  { label: "Business Review", to: "/supply-chain/business-review" },
  { label: "Status Report", to: "/supply-chain/reports" },
  { label: "Tracking", to: "/supply-chain/tracking" },
  { label: "Schedules", to: "/supply-chain/schedules" },
  { label: "Orders", to: "/supply-chain/orders" },
  { label: "SGL Account Management Structure", to: "/supply-chain/account" },
  { label: "Operational Related", to: "/supply-chain/operational" },
  { label: "Detention Monitoring", to: "/supply-chain/detention" },
  { label: "Invoice Additional Charge Approval", to: "/supply-chain/invoice" },
];

const PROCUREMENT_LINKS = [
  { label: "Rate Card", to: "/procurement/rate-card" },
  { label: "Point to Point Quotation Request", to: "/procurement/quotation" },
  { label: "About SGL", to: "/procurement/about" },
];

const MENUS = [
  { key: "procurement", label: "Commercial", prefix: "/procurement", links: PROCUREMENT_LINKS, testid: "nav-procurement", menuTestid: "procurement-menu" },
  { key: "supply", label: "Supply Chain", prefix: "/supply-chain", links: SUPPLY_LINKS, testid: "nav-supply-chain", menuTestid: "supply-chain-menu" },
];

export const AppHeader = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [openMenu, setOpenMenu] = useState(null);
  const onBriefing = pathname === "/";

  return (
    <header className="sticky top-0 z-30 border-b border-border bg-white/85 backdrop-blur-xl">
      <div className="mx-auto max-w-[1400px] px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-home">
            <img src={LOGO} alt="ARB 4x4 Accessories" className="h-8 bg-white rounded px-2 py-1 object-contain" />
            <span className="hidden sm:inline-block h-5 w-px bg-border" />
            <img src={LOGO2} alt="Scan Global Logistics" className="hidden sm:block h-7 bg-white rounded px-1.5 py-1 object-contain" />
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              data-testid="nav-briefing"
              className={`px-3 py-2 text-xs font-mono-plex tracking-widest uppercase transition-colors ${onBriefing ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}
            >
              Market Briefing
            </Link>
            {MENUS.map((m) => {
              const active = pathname.startsWith(m.prefix);
              const open = openMenu === m.key;
              return (
                <div key={m.key} className="relative">
                  <button
                    data-testid={m.testid}
                    onClick={() => setOpenMenu(open ? null : m.key)}
                    className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-mono-plex tracking-widest uppercase transition-colors ${active ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                  >
                    {m.label} <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
                  </button>
                  {open && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setOpenMenu(null)} />
                      <div className="absolute left-0 top-full mt-1 z-20 w-64 border border-border bg-white py-1 shadow-xl" data-testid={m.menuTestid}>
                        {m.links.map((l) => (
                          <button
                            key={l.to}
                            data-testid={`nav-${l.to.split("/").pop()}`}
                            onClick={() => { setOpenMenu(null); navigate(l.to); }}
                            className={`block w-full text-left px-4 py-2.5 text-xs tracking-wide transition-colors ${pathname === l.to ? "text-foreground bg-secondary/60" : "text-muted-foreground hover:text-foreground hover:bg-secondary/40"}`}
                          >
                            {l.label}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </nav>
        </div>
        <div className="flex items-center gap-4">
          <span className="hidden sm:inline text-xs text-muted-foreground" data-testid="dashboard-user-email">{user?.email}</span>
          <button
            onClick={logout}
            data-testid="logout-button"
            className="inline-flex items-center gap-2 border border-border px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground hover:border-muted-foreground/40 transition-colors"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </div>

      {/* mobile nav */}
      <div className="md:hidden border-t border-border px-6 py-2 flex items-center gap-4 overflow-x-auto">
        <Link to="/" className={`text-[11px] font-mono-plex tracking-widest uppercase whitespace-nowrap ${onBriefing ? "text-foreground" : "text-muted-foreground"}`}>Briefing</Link>
        {MENUS.flatMap((m) => m.links).map((l) => (
          <Link key={l.to} to={l.to} className={`text-[11px] font-mono-plex tracking-widest uppercase whitespace-nowrap ${pathname === l.to ? "text-foreground" : "text-muted-foreground"}`}>{l.label}</Link>
        ))}
      </div>
    </header>
  );
};

export default AppHeader;
